import type { Route } from 'next';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import Image from '@/components/image/NextImage';
import { useAnalytics } from '@/hooks/analytics';
import { formatResizedUrl } from '@/utils/app/formatResizedUrl';

import {
	ChevronRightCenterd,
	CopyWrapper,
	ExternalWrapper,
	IconAndCopyWrapper,
	ImageWrapper,
	InternalWrapper,
	Subtitle,
	Title,
} from './styles';
import { ToutPackProps } from './types';

// An internal path starts with a single `/`; a protocol-relative `//host/path` is another
// origin and, like a full https:// url, needs a plain anchor rather than Next's Link. Browsers
// also parse `/\host/path` as protocol-relative, so a leading backslash is rejected the same way.
// Browsers strip ASCII tab, CR and LF before resolving an href, so those must go before
// any prefix check or `/<tab>/host` would pass as internal and resolve as `//host`.
function stripUrlWhitespace(url: string): string {
	return url.replace(/[\t\n\r]/g, '');
}

function isInternalPath(url: string): boolean {
	const normalized = stripUrlWhitespace(url);
	return normalized.startsWith('/') && !normalized.startsWith('//') && !normalized.startsWith('/\\');
}

// The url comes straight from the api and lands directly in an href, so a javascript:/data:/
// vbscript: scheme must be rejected outright rather than merely routed as an external anchor.
function isSafeToutUrl(url: string): boolean {
	if (isInternalPath(url)) {
		return true;
	}
	try {
		const { protocol } = new URL(stripUrlWhitespace(url));
		return protocol === 'http:' || protocol === 'https:';
	} catch {
		return false;
	}
}

// Module-level (not a ref) so an impression survives the unmount/remount PacksList does while
// swapping to a loader during SWR revalidation — a ref would reset and let the same impression
// log twice. Cleared once a client-side navigation completes so a return visit to the page logs
// a fresh impression; tests must reset it between cases.
const viewedKeys = new Set<string>();

function resetToutPackViewedKeys(): void {
	viewedKeys.clear();
}

// Subscribed once for the page's lifetime rather than per tout: the loader swap can leave a
// window with no tout mounted, and a navigation completing in that window must still clear the
// registry. Listening for the completed event means a cancelled navigation leaves it intact.
let routeChangeSubscribed = false;

/** Test-only: returns the module to its never-mounted state between cases. */
export function resetToutPackState(): void {
	viewedKeys.clear();
	routeChangeSubscribed = false;
}

const ToutPack = ({ pack, item, packFeedName }: ToutPackProps) => {
	const { logEvent, isAnalyticsInitialized } = useAnalytics();
	const router = useRouter();

	useEffect(() => {
		if (routeChangeSubscribed) return;
		routeChangeSubscribed = true;
		router.events.on('routeChangeComplete', resetToutPackViewedKeys);
	}, [router.events]);

	// The api blanks pack-level title/subtitle in favor of the item's for banner-promo, and
	// tout packs never set their own title/subtitle in the first place — falling back to the
	// pack's copy covers both without needing to branch on display_type here.
	const title = item.title ?? pack.title;
	const subtitle = item.subtitle ?? pack.subtitle;

	const eventProps = {
		pack_class: pack.pack_class,
		pack_display_type: pack.display_type,
		pack_item_id: item.id ?? null,
		pack_item_title: title ?? null,
		feed_id: packFeedName ?? null,
	};

	// Keyed on the feed and rendered pack item so a re-render, a StrictMode double effect run,
	// or a parent unmount/remount with the same item cannot log a second impression, while the
	// same item shown under a different feed still gets its own attributed impression; a tout
	// with no title, or an unsafe url, renders nothing and must not log one at all.
	const itemKey = `${packFeedName ?? ''}:${pack.id}:${item.id ?? item.url}`;
	const isRenderable = Boolean(title) && isSafeToutUrl(item.url);
	useEffect(() => {
		// Before consent the analytics client drops events, so marking the key then would
		// silence the impression for the rest of the page once consent arrives.
		if (!isAnalyticsInitialized || !isRenderable || viewedKeys.has(itemKey)) {
			return;
		}
		viewedKeys.add(itemKey);
		logEvent({ eventName: 'Pack : Item : Viewed', eventProps });
		// `eventProps` is rebuilt every render from values already covered by `itemKey`; the Set
		// guard above is what makes this fire once, so only the identity inputs are dependencies.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAnalyticsInitialized, isRenderable, itemKey]);

	if (!isRenderable) {
		return null;
	}

	const onClick = () => {
		logEvent({ eventName: 'Pack : Item : Clicked', eventProps });
	};

	const content = (
		<>
			<IconAndCopyWrapper>
				{item.image?.url && (
					<ImageWrapper>
						<Image
							fill
							style={{ objectFit: 'cover' }}
							src={formatResizedUrl(item.image.url, 96, 96)}
							alt=""
							data-testid="tout-pack-image"
						/>
					</ImageWrapper>
				)}
				<CopyWrapper>
					<Title>{title}</Title>
					{subtitle && <Subtitle>{subtitle}</Subtitle>}
				</CopyWrapper>
			</IconAndCopyWrapper>
			<ChevronRightCenterd />
		</>
	);

	if (isInternalPath(item.url)) {
		return (
			<InternalWrapper href={item.url as Route} onClick={onClick}>
				{content}
			</InternalWrapper>
		);
	}

	return (
		<ExternalWrapper href={item.url} rel="noopener noreferrer" onClick={onClick}>
			{content}
		</ExternalWrapper>
	);
};

export default ToutPack;
